/* eslint-disable no-promise-executor-return */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable max-len */
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useSyncTargetsByMdmIdListToVeevaMutation } from '../../basics/graphql/mutations/syncTargetsByMdmIdListToVeeva';
import { useUpdateCepTargetsSyncInfoMutation } from '../../basics/graphql/mutations/updateCepTargetsSyncInfo';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import { mayaCepTemplateCreateRoute, mayaCepTemplateEditRoute } from 'basics/constants/routes.constants';
import { CepStatus, CepSteps, TargetListMode, VeevaSyncStatus } from 'basics/enums/maya.enums';
import { useRemoveTargetsMutation } from 'basics/graphql/mutations/removeTargets';
import { useRemoveTargetsByMdmIdListMutation } from 'basics/graphql/mutations/removeTargetsByMdmIdList';
import { useSetStatusToPendingDeletionMutation } from 'basics/graphql/mutations/setStatusToPendingDeletion';
import { useSyncTargetsToVeevaMutation } from 'basics/graphql/mutations/syncTargetsToVeeva';
import useGetCepByIdLazyQuery from 'basics/graphql/queries/getCepByIdLazy';
import useGetTargetListByCepIdLazyQuery from 'basics/graphql/queries/getTargetListByCepIdLazy';
import { SelectOption } from 'basics/options/options.types';
import { CepStateType } from 'basics/types/maya.types';
import { succesToast, warningToast } from 'basics/utils/toast';
import { CustomerEngagementPlanning, Target } from 'generated/maya.types';
import useCepFormTargetListGroupConfig from 'groups/CepFormTargetListGroup/CepFormTargetListGroup.config';
import cepState from 'states/cep.states';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import { useYodaForm, useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCepFormTargetListGroup = () => {
  const flags = useFlags();
  const { t } = useTranslation();
  const location = useLocation();
  const isTemplate = [mayaCepTemplateCreateRoute, mayaCepTemplateEditRoute].includes(location.pathname);

  const { providerFields } = useYodaCreateForm();

  const cepStateValue = useSnapshot(cepState) as CepStateType;

  const [targetListMode, setTargetListMode] = useState(
    flags.mayaBiogenlinc4307EnableDynamicTargeting ? TargetListMode.dynamic : TargetListMode.upload,
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openRemoveSelectedModal, setOpenRemoveSelectedModal] = useState(false);
  const [syncStatusFilter, setSyncStatusFilter] = useState<VeevaSyncStatus | null>(null);

  const { useWatchStep } = useYodaForm();
  const stepState = useWatchStep(CepSteps.targetListStep);
  const [lastSyncDate, setLastSyncDate] = useState<Date | null>(null);
  const [lastSyncCount, setLastSyncCount] = useState<number | null>(0);
  const [syncExpirationDate, setSyncExpirationDate] = useState<Date | null>(null);
  const [isSyncTimeoutActive, setIsSyncTimeoutActive] = useState<boolean>(false);

  const MIN_TIMEOUT_IN_SECONDS = flags.mayaAkma1292DisableAutoTargetSync?.minTimeoutInSeconds || 5;
  const MAX_TIMEOUT_IN_SECOND = flags.mayaAkma1292DisableAutoTargetSync?.maxTimeoutInSeconds || 60;
  const TIMEOUT_FOR_EVERY_ONE_THOUSAND_CTE_IN_SECONDS = flags.mayaAkma1292DisableAutoTargetSync?.timeoutForEveryOneThousandCTEInSeconds || 5;
  const ADDITIONAL_TIME_FOR_SYNC_IN_SECONDS = flags.mayaAkma1292DisableAutoTargetSync?.additionalTimeForSyncInSeconds || 5;

  useEffect(() => {
    if (!flags.mayaAkma1292DisableAutoTargetSync?.disabled || !lastSyncDate) {
      setSyncExpirationDate(null);
      setIsSyncTimeoutActive(false);
      return;
    }

    const tacticsCount = cepStateValue.tactics?.length || 0;
    const syncedTargetsCount = lastSyncCount || 0;
    const cteCount = tacticsCount * syncedTargetsCount;

    let calculatedTimeout = Math.ceil(cteCount / 1000) * TIMEOUT_FOR_EVERY_ONE_THOUSAND_CTE_IN_SECONDS;
    if (calculatedTimeout < MIN_TIMEOUT_IN_SECONDS) {
      calculatedTimeout = MIN_TIMEOUT_IN_SECONDS;
    } else if (calculatedTimeout > MAX_TIMEOUT_IN_SECOND) {
      calculatedTimeout = MAX_TIMEOUT_IN_SECOND;
    }

    const expirationDate = new Date(new Date(lastSyncDate).getTime() + calculatedTimeout * 1000);

    if (new Date() < expirationDate) {
      setSyncExpirationDate(expirationDate);
      setIsSyncTimeoutActive(true);
    } else {
      setSyncExpirationDate(null);
      setIsSyncTimeoutActive(false);
    }
  }, [flags.mayaAkma1292DisableAutoTargetSync?.disabled, lastSyncDate, lastSyncCount, cepStateValue.tactics?.length]);

  const handleSyncTimeoutComplete = useCallback(() => {
    setIsSyncTimeoutActive(false);
    setSyncExpirationDate(null);
  }, []);

  useEffect(() => {
    const newDateStr = cepState.cep?.lastTargetsSyncAt;
    const newCount = cepState.cep?.lastSyncTargetCount || 0;

    if (!newDateStr) return;

    const newTime = new Date(newDateStr).getTime();
    const prevTime = lastSyncDate ? new Date(lastSyncDate).getTime() : 0;

    if (!lastSyncDate || (newTime > prevTime && newTime - prevTime >= 60000)) {
      setLastSyncDate(newDateStr as Date);
      setLastSyncCount(newCount);
    }
  }, [cepState.cep?.lastTargetsSyncAt, cepState.cep?.lastSyncTargetCount, lastSyncDate]);

  useEffect(() => {
    setTargetListMode(
      !flags.mayaBiogenlinc4307EnableDynamicTargeting || (!cepStateValue.cep?.targetFilter)
        ? TargetListMode.upload
        : TargetListMode.dynamic,
    );
  }, [cepStateValue.cep?.targetFilter, cepStateValue.targetList.length, flags.mayaBiogenlinc4307EnableDynamicTargeting]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  const selectTargetListOnChange = (event: ChangeEvent<HTMLInputElement>, value: string) => {
    setTargetListMode(event.target.value as TargetListMode);
  };

  const {
    cepFormTargetListGroupConfig,
    targetListConfig,
    listOfSelectedMdmId,
    setListOfSelectedMdmId,
    syncStatusTargetListFilterConfig,
  } = useCepFormTargetListGroupConfig(targetListMode, selectTargetListOnChange);

  const [removeTargetList, { loading: removeTargetsLoading }] = useRemoveTargetsMutation();
  const [removeTargetsByMdmIdList, { loading: removeTargetsByMdmIdListLoading }] = useRemoveTargetsByMdmIdListMutation();
  const [setStatusToPendingDeletion, { loading: setStatusToPendingDeletionLoading }] = useSetStatusToPendingDeletionMutation();
  const isLoadingOnRemoving = removeTargetsLoading || removeTargetsByMdmIdListLoading || setStatusToPendingDeletionLoading;
  const [syncTargetsToVeeva] = useSyncTargetsToVeevaMutation();
  const [syncTargetsByMdmIdListToVeeva] = useSyncTargetsByMdmIdListToVeevaMutation();
  const [updateCepTargetsSyncInfo] = useUpdateCepTargetsSyncInfoMutation();
  const [isTooltipForRemoveAllTargetsButton, setIsTooltipForRemoveAllTargetsButton] = useState(false);

  const isCepApproved = cepStateValue.cep?.status === CepStatus.APPROVED;
  const shouldBeDisabled = isCepApproved
    && (!flags.mayaBdcfcsd653ExtendEditingOfApprovedCep || !flags.mayaBdcfcsd1251EnableEditingOfTargetListOfApprovedCep);

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const { queryGetTargetListByCepId } = useGetTargetListByCepIdLazyQuery();
  const { queryGetCepById } = useGetCepByIdLazyQuery();

  const updateTargetList = useCallback(async (options?: { limit?: number; offset?: number; skipSetLoading?: boolean }) => {
    if (!options?.skipSetLoading) setLoading(true);

    const cepId = cepStateValue.cep?._id;
    const limit = options?.limit !== undefined ? options.limit : queryOptions.limit;
    const offset = options?.offset !== undefined ? options.offset : queryOptions.offset;

    const { data: queryData } = await queryGetTargetListByCepId({
      variables: {
        cepId, limit, offset, veevaSyncStatus: syncStatusFilter,
      },
    });

    setRowCountState((prevRowCountState) => queryData?.getTargetListByCepId?.count || prevRowCountState);
    if (!options?.skipSetLoading) setLoading(false);
    cepState.targetList = queryData?.getTargetListByCepId?.targets || [];
  }, [cepStateValue.cep?._id, queryOptions, queryGetTargetListByCepId, syncStatusFilter]);

  useEffect(() => {
    updateTargetList();
  }, [updateTargetList, queryOptions]);

  useEffect(() => {
    setIsTooltipForRemoveAllTargetsButton(
      listOfSelectedMdmId.length > 0
      && flags.mayaAkma740BlockRemovingAllTargetList
      && !shouldBeDisabled,
    );
  }, [listOfSelectedMdmId, shouldBeDisabled, flags]);

  const handleSetStatusToPendingDeletion = async (cepId: string, mdmIds: string[]) => {
    setOpen(false);
    setLoading(true);

    const { data: removeTargetData, error: removeTargetError } = await setStatusToPendingDeletion(cepId, mdmIds);

    if (removeTargetData !== null && !removeTargetError) {
      if (typeof removeTargetData === 'object' && 'modifiedCount' in removeTargetData) {
        succesToast(`Pending deletion ${removeTargetData.modifiedCount} targets and deleted ${removeTargetData.deletedCount} targets`);
      } else {
        warningToast('Invalid response format');
      }
    } else {
      warningToast(removeTargetError || t('errors_standard'));
    }

    const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepId } });
    if (cepState.cep) {
      cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
    }

    updateTargetList();
    setRowCountState(0);
    setLoading(false);
    setListOfSelectedMdmId([]);
  };

  const handleRemoveTargets = async () => {
    const cepId = cepStateValue.cep?._id;

    if (flags.mayaAkma1292DisableAutoTargetSync?.disabled && cepId) {
      setOpen(false);
      setLoading(true);

      const { data } = await queryGetTargetListByCepId({
        variables: {
          cepId, limit: 5000, offset: 0, veevaSyncStatus: null,
        },
      });
      const allMdmIds = data?.getTargetListByCepId?.targets?.map((target: Target) => target.mdmId) || [];
      await handleSetStatusToPendingDeletion(cepId, allMdmIds);
      return;
    }

    setOpen(false);
    setLoading(true);

    const { data: removeTargetData, error: removeTargetError } = await removeTargetList(cepId);

    if (removeTargetData !== null && !removeTargetError) {
      succesToast(t('maya_cep_target_remove_sucess'));
    } else {
      warningToast(removeTargetError || t('errors_standard'));
    }

    if (cepId) {
      const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepId } });
      if (cepState.cep) {
        cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
      }
    }

    updateTargetList();
    setRowCountState(0);
    setLoading(false);
    setListOfSelectedMdmId([]);
  };

  const recalculatePagesAfterTargetsDeletion = (mdmIds: string[], skipSetLoading = false) => {
    setRowCountState((prev) => prev - mdmIds.length);
    const newTotal = rowCountState - mdmIds.length;
    const maxPage = Math.ceil(newTotal / pageSize) - 1;
    let newPage = page;

    if (page > maxPage) {
      newPage = maxPage >= 0 ? maxPage : 0;
      setPage(newPage);
    }

    updateTargetList({ limit: pageSize, offset: pageSize * newPage, skipSetLoading });
  };

  const delayIfAutoTargetSyncDisabled = async () => {
    if (flags.mayaAkma1292DisableAutoTargetSync?.disabled) {
      await new Promise((resolve) => setTimeout(resolve, ADDITIONAL_TIME_FOR_SYNC_IN_SECONDS * 1000));
    }
  };

  const handleSyncTargets = async (cepId: string) => {
    setLoading(true);

    const { data: pendingDeletionData } = await queryGetTargetListByCepId({
      variables: {
        cepId, limit: 5000, offset: 0,
      },
    });
    const mdmIdsForDeletion: string[] = pendingDeletionData?.getTargetListByCepId?.targets?.filter((target: Target) => target.veevaSyncStatus === VeevaSyncStatus.PENDING_DELETION)
      .map((target: Target) => target.mdmId) || [];

    if (mdmIdsForDeletion.length > 0) {
      const {
        data: removeTargetByMdmIdListData,
        error: removeTargetByMdmIdListError,
      } = await removeTargetsByMdmIdList(mdmIdsForDeletion, cepId);

      if (removeTargetByMdmIdListData !== null && !removeTargetByMdmIdListError) {
        const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepId } });
        if (cepState.cep) {
          cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
        }

        recalculatePagesAfterTargetsDeletion(mdmIdsForDeletion, true);
        succesToast(t('maya_cep_target_remove_selected_success'));
      } else {
        warningToast(removeTargetByMdmIdListError || t('errors_standard'));
      }
    }

    const { data: syncTarget } = await syncTargetsToVeeva(cepId);

    if (syncTarget && Array.isArray(syncTarget?.errors) && syncTarget?.errors?.length > 0) {
      syncTarget.errors.forEach((error) => {
        warningToast(error);
      });
    } else {
      await delayIfAutoTargetSyncDisabled();
      succesToast(t('maya_cep_targets_sync_success'));
    }

    const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepStateValue.cep?._id } });
    if (cepState.cep) {
      cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
    }

    await updateCepTargetsSyncInfo(cepId, pendingDeletionData?.getTargetListByCepId?.targets?.length || 0);
    updateTargetList();
    setLastSyncCount(pendingDeletionData?.getTargetListByCepId?.targets?.length);
    setLastSyncDate(new Date());
    setLoading(false);
    setListOfSelectedMdmId([]);
  };

  const handleRemoveSelectedTargets = async () => {
    const cepId = cepStateValue.cep?._id;

    setOpenRemoveSelectedModal(false);
    setLoading(true);

    if (flags.mayaAkma1292DisableAutoTargetSync?.disabled && cepId) {
      await handleSetStatusToPendingDeletion(cepId, listOfSelectedMdmId);
      setListOfSelectedMdmId([]);
      return;
    }

    const {
      data: removeTargetByMdmIdListData,
      error: removeTargetByMdmIdListError,
    } = await removeTargetsByMdmIdList(listOfSelectedMdmId, cepId);

    if (removeTargetByMdmIdListData !== null && !removeTargetByMdmIdListError) {
      const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepId } });
      if (cepState.cep) {
        cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
      }

      recalculatePagesAfterTargetsDeletion(listOfSelectedMdmId, true);
      succesToast(t('maya_cep_target_remove_selected_success'));
    } else {
      warningToast(removeTargetByMdmIdListError || t('errors_standard'));
    }

    setLoading(false);
    setListOfSelectedMdmId([]);
  };

  const handleSyncSelectedTargets = async (cepId: string) => {
    setLoading(true);

    const mdmIdsForAdding: string[] = [];
    const mdmIdsForDeletion: string[] = [];

    cepStateValue?.targetList?.forEach((target) => {
      if (listOfSelectedMdmId.includes(target.mdmId)) {
        target.veevaSyncStatus === VeevaSyncStatus.PENDING_DELETION ? mdmIdsForDeletion.push(target.mdmId) : mdmIdsForAdding.push(target.mdmId);
      }
    });

    if (mdmIdsForAdding.length > 0) {
      const { data: syncTarget } = await syncTargetsByMdmIdListToVeeva(cepId, mdmIdsForAdding);

      if (syncTarget && Array.isArray(syncTarget?.errors) && syncTarget?.errors?.length > 0) {
        syncTarget.errors.forEach((error) => {
          warningToast(error);
        });
      } else {
        await delayIfAutoTargetSyncDisabled();
        succesToast(t('maya_cep_selected_targets_sync_success'));
      }
    }

    if (mdmIdsForDeletion.length > 0) {
      const {
        data: removeTargetByMdmIdListData,
        error: removeTargetByMdmIdListError,
      } = await removeTargetsByMdmIdList(mdmIdsForDeletion, cepId);

      if (removeTargetByMdmIdListData !== null && !removeTargetByMdmIdListError) {
        const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepId } });
        if (cepState.cep) {
          cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
        }

        recalculatePagesAfterTargetsDeletion(mdmIdsForDeletion, true);
        succesToast(t('maya_cep_target_remove_selected_success'));
      } else {
        warningToast(removeTargetByMdmIdListError || t('errors_standard'));
      }
    } else {
      updateTargetList({ skipSetLoading: true });
    }

    await updateCepTargetsSyncInfo(cepId, (mdmIdsForAdding.length + mdmIdsForDeletion.length) || 0);
    setLastSyncCount((mdmIdsForAdding.length + mdmIdsForDeletion.length) || 0);
    setLastSyncDate(new Date());
    setLoading(false);
    setListOfSelectedMdmId([]);
  };

  const handleChangeStatusTargetsField = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption) => {
    if (value?.id) {
      if (value.id === 'All') {
        setSyncStatusFilter(null);
      } else {
        setSyncStatusFilter(value.id as VeevaSyncStatus);
      }
    }

    setListOfSelectedMdmId([]);
    setRowCountState(0);
    setPage(0);
  }, [setSyncStatusFilter, setListOfSelectedMdmId]);

  const handleSelectAllTargets = () => {
    if (listOfSelectedMdmId.length === cepState.targetList.length) {
      setListOfSelectedMdmId([]);
    } else {
      const selectedTargetsMdmIds = cepState.targetList.map((target) => target.mdmId);
      setListOfSelectedMdmId((prev) => {
        return [...new Set([...prev, ...selectedTargetsMdmIds])];
      });
    }
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const syncTargetsButtonConfig = {
    buttonProps: {
      onClick: () => handleSyncTargets(cepStateValue.cep?._id || ''),
      buttonType: ButtonType.primary,
      disabled: shouldBeDisabled || isSyncTimeoutActive || isTemplate,
      fullWidth: true,
    },
    label: t('maya_targetlist_sync_label'),
    loading: loading || removeTargetsLoading,
    width: 240,
  };

  const syncSelectedTargetsButtonConfig = {
    buttonProps: {
      onClick: () => handleSyncSelectedTargets(cepStateValue.cep?._id || ''),
      buttonType: ButtonType.primary,
      disabled: shouldBeDisabled || listOfSelectedMdmId.length === 0 || isSyncTimeoutActive || isTemplate,
      fullWidth: true,
    },
    label: t('maya_targetlist_sync_selected_label'),
    loading: loading || removeTargetsLoading,
    width: 240,
  };

  const openModalButtonConfig = {
    buttonProps: {
      onClick: () => setOpen(true),
      buttonType: ButtonType.outlined,
      disabled: shouldBeDisabled || (flags.mayaAkma740BlockRemovingAllTargetList && listOfSelectedMdmId.length > 0),
      fullWidth: true,
    },
    label: t('maya_targetlist_remove_label'),
    loading: loading || isLoadingOnRemoving,
    width: 240,
  };

  const removeSelectedTargetsModalButtonConfig = {
    buttonProps: {
      onClick: () => setOpenRemoveSelectedModal(true),
      buttonType: ButtonType.outlined,
      disabled: shouldBeDisabled || listOfSelectedMdmId.length === 0,
      fullWidth: true,
    },
    label: t('maya_targetlist_remove_selected_label'),
    loading: loading || isLoadingOnRemoving,
    width: 240,
  };

  const selectAllTargetsButtonConfig = {
    buttonProps: {
      onClick: () => handleSelectAllTargets(),
      buttonType: ButtonType.outlined,
      disabled: shouldBeDisabled || cepState.targetList.length === 0,
    },
    label: t('maya_cep_form_target_list_select_all_targets_label'),
    loading: loading || isLoadingOnRemoving,
  };

  return {
    cepFormTargetListGroupConfig,
    data: Object.values(cepStateValue.targetList),
    handleRemoveTargets,
    loading,
    onPageChange,
    onPageSizeChange,
    open,
    openModalButtonConfig,
    removeTargetsLoading,
    rowCountState,
    setLoading,
    setOpen,
    stepIsValid: stepState?.isValid || false,
    selectAllTargetsButtonConfig,
    syncTargetsButtonConfig,
    syncSelectedTargetsButtonConfig,
    t,
    targetListConfig,
    targetListMode,
    updateTargetList,
    removeSelectedTargetsModalButtonConfig,
    openRemoveSelectedModal,
    setOpenRemoveSelectedModal,
    handleRemoveSelectedTargets,
    syncStatusTargetListFilterConfig,
    handleChangeStatusTargetsField,
    providerFields,
    isTooltipForRemoveAllTargetsButton,
    syncExpirationDate,
    handleSyncTimeoutComplete,
  };
};

export default useCepFormTargetListGroup;
